/**
 *
 * BulkActionQueue
 *
 */

import {
  CollectionOutlineIcon,
  Icon,
  XOutlineIcon,
} from '@fountain/ripple/icon';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { classNames } from 'react-extras';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { fetchJobDetailInitAction } from 'containers/ApplicantsV2/actions';
import { makeSelectBulkActionSubmitting } from 'containers/ApplicantTableV2/selectors';
import { makeSelectExternalId } from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import useApplicants, { getQueryParams } from 'hooks/useApplicants';
import useFetchBulkActionsQueue from 'hooks/useFetchBulkActionsQueue';
import usePusherChannel from 'hooks/usePusherChannel';
import { history } from 'utils/history';
import { useInjectReducer } from 'utils/injectReducer';
import { SELECT_ALL_COUNT_EXCEEDED_ERROR_TYPE } from 'utils/selectAllCountRefusal';
import { matchApplicantsListPath } from 'utils/urlUtils';

import { closeBulkActionQueueMenu, openBulkActionQueueMenu } from './actions';
import BulkActionQueuedItem from './components/BulkActionQueuedItem';
import { BULK_ACTION_STATUS, BULK_TYPE } from './constants';
import messages from './messages';
import reducer from './reducer';
import { makeSelectBulkActionQueue } from './selectors';
import useStyles from './styles';

/**
 * @return {null}
 */
export function BulkActionQueue({ intl }) {
  useInjectReducer({ key: 'bulkActionQueue', reducer });

  const location = useLocation();
  const bulkActionQueue = useSelector(makeSelectBulkActionQueue());
  const userExternalId = useSelector(makeSelectExternalId());
  const bulkActionSubmitting = useSelector(makeSelectBulkActionSubmitting());
  const { isBulkActionQueueMenuOpen } = bulkActionQueue;

  const dispatch = useDispatch();
  const { query, filter_id: filterId } = queryString.parse(location.search);

  const { fetchApplicants } = useApplicants();

  const { initialBulkActions, isFetching } = useFetchBulkActionsQueue();

  const anchorElRef = useRef();

  const [bulkActions, setBulkActions] = useState({});
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const classes = useStyles();

  const channelName = `bulk_action;user;${userExternalId}`;

  const callback = data => {
    switch (data.status) {
      case BULK_ACTION_STATUS.created:
        return handleOpenBulkActionQueueMenu();
      case BULK_ACTION_STATUS.pending:
      case BULK_ACTION_STATUS.running:
      case BULK_ACTION_STATUS.errored:
      case BULK_ACTION_STATUS.paused: {
        // A refused select-all already shows Hire's message from its request.
        const refusedSelectAll =
          data.arguments?.[SELECT_ALL_COUNT_EXCEEDED_ERROR_TYPE] != null;
        if (data.status === BULK_ACTION_STATUS.errored && !refusedSelectAll) {
          dispatch(
            addMessageAction(
              `Bulk Action Error - ${data.error_message}`,
              'error',
            ),
          );
        }

        return setBulkActions(previous => {
          const prevData = previous[data.id];
          return {
            ...previous,
            [data.id]: {
              ...data,
              user_name: data.user_name || (prevData && prevData.user_name),
              start_time: data.start_time || (prevData && prevData.start_time),
            },
          };
        });
      }
      case BULK_ACTION_STATUS.complete: {
        setBulkActions(previous => ({ ...previous, [data.id]: data }));
        if (
          data.type !== 'BulkMessage' &&
          data.type !== BULK_TYPE.BulkDocumentExport
        ) {
          const queryParams = getQueryParams({
            location: history.location,
          });
          const match = matchApplicantsListPath(history.location.pathname);
          if (match?.kind === 'master') {
            fetchApplicants(queryParams);
          } else if (match?.kind === 'stage') {
            dispatch(fetchJobDetailInitAction(match.jobId, query, filterId));
            fetchApplicants(queryParams);
          }
        }
        return handleOpenBulkActionQueueMenu();
      }
      case BULK_ACTION_STATUS.deleted: {
        return setBulkActions(previous => {
          const newState = { ...previous };
          delete newState[data.id];
          return newState;
        });
      }
      default:
        return null;
    }
  };

  const bulkActionsListEndRef = useRef(null);

  // Nothing connects until this user has a bulk action in flight or submits
  // one. The `created` event for a submission lands after `bulkActionSubmitting`
  // clears, so once listening starts it stays on for the rest of the session.
  const hasBulkActionInProgress = Object.values(bulkActions).some(
    action => action.status !== BULK_ACTION_STATUS.complete,
  );
  const [isListening, setIsListening] = useState(false);
  useEffect(() => {
    if (hasBulkActionInProgress || bulkActionSubmitting) setIsListening(true);
  }, [hasBulkActionInProgress, bulkActionSubmitting]);

  usePusherChannel(isListening && userExternalId ? channelName : null, {
    status_update: callback,
  });

  useEffect(() => {
    if (isListening) scrollToBottom();
  }, [isListening]);

  useEffect(() => {
    if (initialBulkActions) setBulkActions(initialBulkActions);
  }, [initialBulkActions]);

  const handleOpenBulkActionQueueMenu = () =>
    dispatch(openBulkActionQueueMenu());
  const handleCloseBulkActionQueueMenu = () =>
    dispatch(closeBulkActionQueueMenu());

  const toggleBulkActionsWidget = () => {
    if (isBulkActionQueueMenuOpen) {
      return handleCloseBulkActionQueueMenu();
    }

    return handleOpenBulkActionQueueMenu();
  };

  const scrollToBottom = () => {
    if (bulkActionsListEndRef && bulkActionsListEndRef.current) {
      bulkActionsListEndRef.current.scrollIntoView();
    }
  };

  const handleDragMove = e => {
    setIsDragging(true);
    setTranslate({
      x: translate.x + e.movementX,
      y: translate.y + e.movementY,
    });
  };

  const handleDragStop = () => {
    setIsDragging(false);
  };

  if (Object.keys(bulkActions).length < 1 || isFetching) return null;

  return (
    <Draggable
      bounds="body"
      handle=".draggableBulkActionQueue"
      onStop={handleDragStop}
      onDrag={handleDragMove}
    >
      <div className={`${classes.bulkActionQueue} draggableBulkActionQueue`}>
        <IconButton
          title={intl.formatMessage(messages.bulkJobs)}
          aria-label={intl.formatMessage(messages.bulkJobs)}
          aria-expanded={isBulkActionQueueMenuOpen}
          classes={{ root: classes.trigger }}
          className={classNames({
            [classes.closeWidget]: isBulkActionQueueMenuOpen,
          })}
          onClick={toggleBulkActionsWidget}
        >
          {!isBulkActionQueueMenuOpen ? (
            <Icon icon={CollectionOutlineIcon} size="sm" color="neutral-400" />
          ) : (
            <Icon icon={XOutlineIcon} size="xs" color="white" />
          )}
        </IconButton>
        <Menu
          style={{
            transform: `translateX(${translate.x}px) translateY(${translate.y}px)`,
          }}
          classes={{ paper: classes.bulkActionsList }}
          MenuListProps={{
            disablePadding: true,
          }}
          onEntered={scrollToBottom}
          anchorEl={anchorElRef}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{ vertical: -8, horizontal: 'right' }}
          getContentAnchorEl={null}
          open={!isDragging && isBulkActionQueueMenuOpen}
          onClose={toggleBulkActionsWidget}
        >
          {Object.keys(bulkActions).map(bulkAction => (
            <BulkActionQueuedItem
              key={bulkActions[bulkAction].id}
              data={bulkActions[bulkAction]}
            />
          ))}
          <div ref={bulkActionsListEndRef} />
        </Menu>
      </div>
    </Draggable>
  );
}

export default injectIntl(BulkActionQueue);

BulkActionQueue.propTypes = {
  intl: PropTypes.object.isRequired,
};
